import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const SERVICES = [
  {
    no: "01",
    title: "Education",
    hindi: "Padhai",
    tagline: "Gyaan wahi jo sach mein kaam aaye — kitabi gyaan nahi, life badalne ka zariya.",
    desc: "True education goes beyond the classroom. It is about developing a critical mind and the confidence to navigate a changing world. We focus on holistic learning that turns knowledge into wisdom.",
    points: ["Concept-first teaching", "Simple language (Saral Bhasha)", "Wisdom over rote learning"],
    img: "/images/girl-with-light.jpeg ", //"https://images.unsplash.com/photo-1749700332031-cf99864959ea",
    accent: "text-chai-red",
  },
  {
    no: "02",
    title: "Employability",
    hindi: "Kaamyabi",
    tagline: "Kitabi Gyan ke Saath Jaroori Hai Jeevan Ka Hunar.",
    desc: "In today's competitive landscape, a degree is just the entry ticket. To truly thrive, you need industry-relevant skills and a professional edge. We bridge academic learning and real-world employment.",
    points: ["Industry-relevant skills", "Spoken English / IELTS / PTE", "Interview & resume prep", "Job-ready confidence"],
    img: "/images/photo-1522202176988-66273c2fd55f.jpeg", //"https://images.unsplash.com/photo-1522202176988-66273c2fd55f",
    accent: "text-chai-warmth",
  },
  {
    no: "03",
    title: "Motivation",
    hindi: "Inspiration",
    tagline: "Bheed ka hissa nahi, solution ka zariya bano — khud par bharosa rakhiye.",
    desc: "Success isn't just about talent; it's about the grit to keep going. Motivation is the spark that gets you started, but consistency is what finishes the race. Turn every setback into a stepping stone.",
    points: ["Mindset coaching", "Real success stories", "Consistency systems"],
    img: "/images/photo-1501743411739-de52ea0ce6a0.jpeg", //"https://images.unsplash.com/photo-1501743411739-de52ea0ce6a0",
    accent: "text-chai-red",
  },
  {
    no: "04",
    title: "Travels",
    hindi: "Udaan",
    tagline: "Travel Simplified. Memories Amplified. Bas Udaan ke saath.",
    desc: "Safar sirf manzil tak pahunchna nahi, balki raaste mein khud ko paana hai. Udaan ke saath travel hota hai simplified — naye sheher, naye nazare, aur naye log. Har journey ek nayi kahani, har stop ek nayi yaad. Chalo, duniya ko explore karo aur apni zindagi ko amplify karo. This is Udaan – Safar Simplified.",
    points: ["Curated, simplified journeys", "Naye sheher, naye nazare, naye log", "Har stop ek nayi yaad"],
    img: "/images/photo-1469854523086-cc02fe5d8800.avif?auto=format&fit=crop&w=1400&q=80", //"https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=1400&q=80",
    accent: "text-chai-warmth",
  },
];

const FRAMES = [
  "/images/pexels-photo-16029823.jpeg?auto=compress&cs=tinysrgb&w=1400", //"https://images.pexels.com/photos/16029823/pexels-photo-16029823.jpeg?auto=compress&cs=tinysrgb&w=1400",
  "/images/photo-1506863530036-1efeddceb993.avif?auto=format&fit=crop&w=1400&q=80", //"https://images.unsplash.com/photo-1506863530036-1efeddceb993?auto=format&fit=crop&w=1400&q=80",
  "/images/photo-1524504388940-b1c1722653e1.avif?auto=format&fit=crop&w=1400&q=80", //"https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=1400&q=80",
  "/images/photo-1500648767791-00dcc994a43e.avif?auto=format&fit=crop&w=1400&q=80", //https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=1400&q=80",
];

function FramesByChaied() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const x = useTransform(scrollYProgress, [0, 1], ["2%", "-30%"]);

  return (
    <section ref={ref} className="bg-chai-ink text-white py-24 md:py-32 overflow-hidden" data-testid="frames-section">
      <div className="w-full max-w-none px-4 md:px-8 lg:px-12 mb-14">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-chai-yellow mb-6">A Creative Studio</p>
          <h2 className="font-heading text-6xl md:text-8xl font-bold tracking-tighter leading-none">
            Frames By <span className="text-chai-yellow">Chaied</span>
          </h2>
          <p className="font-body text-lg text-white/60 mt-6 max-w-xl">
            Beyond the classroom — storytelling through the lens. Portraits, events and moments captured with a deliberate, editorial eye.
          </p>
        </Reveal>
      </div>

      <motion.div style={{ x }} className="flex gap-6 pl-4 md:pl-8 lg:pl-12">
        {FRAMES.map((src, i) => (
          <div key={i} className="relative shrink-0 w-[70vw] md:w-[28vw] aspect-[3/4] overflow-hidden border border-white/20">
            <img src={src} alt={`Frame ${i + 1}`} className="w-full h-full object-cover" />
            <span className="absolute top-4 left-4 font-mono text-xs text-white/70">/0{i + 1}</span>
          </div>
        ))}
      </motion.div>

      <div className="w-full max-w-none px-4 md:px-8 lg:px-12 mt-14">
        <Link to="/gallery" data-testid="frames-gallery-link" className="group inline-flex items-center gap-3 border border-white/30 px-8 py-4 font-mono text-xs uppercase tracking-widest hover:bg-chai-yellow hover:text-chai-ink hover:border-chai-yellow transition-colors">
          View The Full Gallery
          <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </section>
  );
}

export default function Services() {
  return (
    <div data-testid="services-page">
      <section className="w-full max-w-none px-4 md:px-8 lg:px-12 pt-40 pb-16">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-chai-red mb-6">What We Do</p>
          <h1 className="font-heading font-bold uppercase tracking-tighter leading-[0.85] text-[13vw] md:text-[8vw]">
            Services
          </h1>
        </Reveal>
      </section>

      {SERVICES.map((s, i) => (
        <section key={s.no} className="w-full max-w-none px-4 md:px-8 lg:px-12 py-16 border-t border-chai-ink/15" data-testid={`service-${s.title.toLowerCase()}`}>
          <div className={`grid grid-cols-1 md:grid-cols-12 gap-10 items-center ${i % 2 === 1 ? "md:[direction:rtl]" : ""}`}>
            <div className="md:col-span-6 [direction:ltr]">
              <Reveal>
                <div className="flex items-center gap-4 mb-4">
                  <span className={`font-mono text-lg ${s.accent}`}>{s.no}</span>
                  <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{s.hindi}</span>
                </div>
                <h2 className="font-heading text-6xl md:text-8xl font-bold tracking-tighter leading-none">{s.title}</h2>
                <p className="font-serif italic text-2xl md:text-3xl mt-5 text-chai-ink">{s.tagline}</p>
                <p className="font-body text-lg text-muted-foreground mt-5 max-w-xl">{s.desc}</p>
                <ul className="mt-8 space-y-3">
                  {s.points.map((pt) => (
                    <li key={pt} className="flex items-center gap-3 font-body text-lg">
                      <span className="flex items-center justify-center w-6 h-6 bg-chai-yellow text-chai-ink shrink-0"><Check size={14} /></span>
                      {pt}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
            <div className="md:col-span-6 [direction:ltr]">
              <Reveal delay={0.1}>
                <div className="relative aspect-[4/3] overflow-hidden border-2 border-chai-ink group">
                  <img src={s.img} alt={s.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      ))}

      <FramesByChaied />
    </div>
  );
}
